import React from "react";
import { Button, Dropdown, Menu } from "antd";
import { EditOutlined } from "@ant-design/icons";
import RejectIcon from "../../assets/images/Reject.svg";
import AcceptIcon from "../../assets/images/AcceptIcon.svg";

const modifyItemsButtonStyle = {
  height: "28px",
  fontSize: "12px",
  borderRadius: "6px",
  backgroundColor: "#2F3856",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
};

// TSK-4875: wireframe 11a -- green, bold, "Generate Bill ✓".
const generateBillButtonStyle = {
  ...modifyItemsButtonStyle,
  backgroundColor: "#1F8A4C",
  borderColor: "#1F8A4C",
  fontWeight: 600,
};

const ActionButtons = ({
  selectedOrder,
  loading,
  onAccept,
  onReject,
  onMarkAsReady,
  onComplete,
  onModifyItems,
  onGenerateBill,
  allowModification,
  billsOnAccept,
  showBilledHint,
  showGenerateBill,
  generatingBill,
  hasOutOfStock,
  rejectReasons,
}) => {
  const rejectMenu = (
    <Menu onClick={({ key }) => onReject(key)}>
      {rejectReasons.map((reason) => (
        <Menu.Item key={reason} style={{ fontSize: "12px" }}>
          {reason.replace(/_/g, " ").toUpperCase()}
        </Menu.Item>
      ))}
    </Menu>
  );

  if (!selectedOrder) return null;

  // AK/FR: the status button, then Modify Items and (TSK-4875, onBill mode)
  // Generate Bill beside it when enabled.
  const renderWithSecondaryButtons = (primaryButton) => {
    const modifyButton = allowModification && (
      <Button key="modify" onClick={onModifyItems} style={modifyItemsButtonStyle} disabled={loading || generatingBill}>
        <EditOutlined />
        Modify Items
      </Button>
    );
    const billButton = showGenerateBill && (
      <Button key="bill" onClick={onGenerateBill} style={generateBillButtonStyle} loading={generatingBill} disabled={loading || generatingBill}>
        {generatingBill ? "Generating..." : "Generate Bill ✓"}
      </Button>
    );
    // TSK-4875 (wireframe 11a/11b): onBill order is Modify Items, status button,
    // Generate Bill. Without Generate Bill the row stays as today (status first).
    const buttons = (showGenerateBill ? [modifyButton, primaryButton, billButton] : [primaryButton, modifyButton]).filter(Boolean);
    if (buttons.length === 1) {
      return (
        <>
          {primaryButton}
          {/* TSK-4875 (wireframe 11c) */}
          {showBilledHint && <div style={{ flex: "0 0 100%", fontSize: "10px", color: "#6b7280" }}>Order is billed. Modify Items and Generate Bill are no longer available.</div>}
        </>
      );
    }
    return (
      <>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${buttons.length}, 1fr)`, gap: "8px" }}>
          {buttons.map((button, index) => (
            <React.Fragment key={index}>{button}</React.Fragment>
          ))}
        </div>
        {/* TSK-4875 (wireframe 11a/11b): what Generate Bill will do. Full-width
            flex item so it wraps onto its own line under the button row. */}
        {showGenerateBill && (
          <div style={{ flex: "0 0 100%", fontSize: "10px", color: "#6b7280" }}>
            {hasOutOfStock ? "Out-of-stock items can't be billed — modify items first" : "Creates shipment + invoice. Stock will be deducted immediately."}
          </div>
        )}
      </>
    );
  };

  switch (selectedOrder.status) {
    case "NEW":
      return (
        <>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${allowModification ? 3 : 2}, 1fr)`, gap: "8px" }}>
            <Dropdown overlay={rejectMenu} trigger={["click"]}>
              <Button
                style={{
                  height: "28px",
                  fontSize: "12px",
                  borderRadius: "6px",
                  border: "1px solid #dc2626",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
                disabled={loading}
              >
                <img src={RejectIcon} style={{ height: "16px" }} alt="Reject" />
                Reject
              </Button>
            </Dropdown>
            {/* TSK-4875: NEW-order modify (onAccept / onBill modes only -- index.js decides). */}
            {allowModification && (
              <Button onClick={onModifyItems} style={modifyItemsButtonStyle} disabled={loading}>
                <EditOutlined />
                Modify Items
              </Button>
            )}
            <Button
              onClick={onAccept}
              style={{
                height: "28px",
                fontSize: "12px",
                borderRadius: "6px",
                backgroundColor: "#2F3856",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
              disabled={loading}
            >
              <img src={AcceptIcon} style={{ height: "16px" }} alt="Accept" />
              {billsOnAccept ? "Accept & Create Order" : "Accept"}
            </Button>
          </div>
          {/* TSK-4875 (wireframe 11d): onAccept bills on Accept. */}
          {billsOnAccept && (
            <div style={{ flex: "0 0 100%", fontSize: "10px", color: "#6b7280" }}>
              Accepting creates order + shipment + invoice immediately.{hasOutOfStock ? " Modify OOS items first." : ""}
            </div>
          )}
        </>
      );
    case "AK": {
      const markAsReadyButton = (
        <Button
          onClick={onMarkAsReady}
          style={{
            height: "28px",
            fontSize: "12px",
            borderRadius: "6px",
            backgroundColor: "#E99013",
            color: "#fff",
          }}
          disabled={loading || generatingBill}
        >
          Mark as Ready
        </Button>
      );
      return renderWithSecondaryButtons(markAsReadyButton);
    }
    case "FR": {
      const dispatchButton = (
        <Button
          onClick={onComplete}
          style={{
            height: "28px",
            fontSize: "12px",
            borderRadius: "6px",
            backgroundColor: "#008000",
            color: "#fff",
          }}
          disabled={loading || generatingBill}
        >
          Dispatch
        </Button>
      );
      return renderWithSecondaryButtons(dispatchButton);
    }
    case "DI":
      return null;
    default:
      return null;
  }
};

export default ActionButtons;
